/**
 * PatrolFlow - GPS Security Management System
 * Copyright © 2025 Tom Hough, PatrolFlow. All rights reserved.
 *
 * CONFIDENTIAL AND PROPRIETARY
 *
 * This source code is the exclusive property of Tom Hough and PatrolFlow.
 * Unauthorized copying, modification, distribution, or use of this software,
 * via any medium, is strictly prohibited without explicit written permission
 * from the copyright holder.
 *
 * For licensing inquiries: tom@patrolflow.ke
 *
 * @author Tom Hough
 * @company PatrolFlow
 * @website https://patrolflow.ke
 */
import { QueryClient, QueryFunction } from "@tanstack/react-query";

type ApiErrorPayload = {
  code?: string;
  message?: string;
};

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

function notifySubscriptionLapsed(message: string) {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("subscription-lapsed", {
      detail: { message, billingPath: "/billing" },
    }),
  );
}

async function throwIfResNotOk(res: Response) {
  if (res.ok) return;

  const text = await res.text();
  let payload: ApiErrorPayload | undefined;

  if (text) {
    try {
      payload = JSON.parse(text) as ApiErrorPayload;
    } catch {
      // Non-JSON responses remain readable through their raw response text.
    }
  }

  const message = payload?.message || text || res.statusText || `Request failed with status ${res.status}`;
  const error = new ApiRequestError(message, res.status, payload?.code);

  if (res.status === 402 && payload?.code === "subscription_lapsed") {
    notifySubscriptionLapsed(message);
  }

  throw error;
}

export async function apiRequest<T = any>(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: {
      ...(data ? { "Content-Type": "application/json" } : {}),
      Accept: "application/json",
    },
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
    mode: "cors",
    cache: "no-cache",
  });

  await throwIfResNotOk(res);
  return res.json();
}

type UnauthorizedBehavior = "returnNull" | "throw";

export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const headers = {
      Accept: "application/json",
    };

    const res = await fetch(queryKey[0] as string, {
      credentials: "include",
      mode: "cors",
      headers,
      cache: "no-cache",
    });
    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }
    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 minutes instead of Infinity for better cache management
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (
          error?.message?.includes("Unauthorized") ||
          error?.message?.includes("Forbidden")
        ) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

// Preload common queries
export const preloadCommonQueries = () => {
  queryClient.prefetchQuery({
    queryKey: ["/api/auth/user"],
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};
